import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Users, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Search,
  Trash2,
  UserCheck,
  UserX,
  Loader2,
  RefreshCw,
  ShieldX,
  Sparkles,
  Zap
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GLOBAL_ADMIN_EMAIL = "matgeo.2008@gmail.com";

export default function AdminUsersPage({ user }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, trial_active: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [updating, setUpdating] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [earlySignups, setEarlySignups] = useState([]);
  const [showEarly, setShowEarly] = useState(false);
  const [showTrials, setShowTrials] = useState(true);

  useEffect(() => {
    // Check if user is admin
    if (user?.email !== GLOBAL_ADMIN_EMAIL) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    fetchUsers();
    fetchEarlySignups();
  }, [user]);

  // Access denied screen
  if (accessDenied) {
    return (
      <DashboardLayout user={user}>
        <div className="flex flex-col items-center justify-center h-[60vh] text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
            <ShieldX className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-zinc-400 mb-6">You don't have permission to access this page.</p>
          <Button onClick={() => navigate("/dashboard")} className="bg-white text-black hover:bg-zinc-200">
            Go to Dashboard
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/users`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setStats({
          total: data.total,
          pending: data.pending,
          approved: data.approved,
        });
      } else {
        toast.error("Failed to load users");
      }
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchEarlySignups = async () => {
    try {
      const token = localStorage.getItem("roas_session_token");
      const headers = token ? { "Authorization": `Bearer ${token}` } : {};
      const resp = await fetch(`${API}/early/admin/list`, { credentials: "include", headers });
      if (resp.ok) setEarlySignups(await resp.json());
    } catch (e) { console.error("Error:", e); }
  };

  const handleApprove = async (userId, approve) => {
    setUpdating(userId);
    try {
      const response = await fetch(`${API}/admin/users/approve`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          user_id: userId,
          registered: approve ? "Y" : "N",
        }),
      });

      if (response.ok) {
        toast.success(approve ? "User approved" : "User approval revoked");
        fetchUsers();
      } else {
        toast.error("Failed to update user");
      }
    } catch (error) {
      toast.error("Failed to update user");
    } finally {
      setUpdating(null);
    }
  };

  const handleDelete = async (userId) => {
    setDeleting(true);
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("User deleted");
        setDeleteDialog(null);
        fetchUsers();
      } else {
        toast.error("Failed to delete user");
      }
    } catch (error) {
      toast.error("Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl text-white font-bold mb-2">
              User Management
            </h1>
            <p className="text-zinc-400">
              Manage user registrations and access
            </p>
          </div>
          <Button
            onClick={fetchUsers}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#121212] border border-white/10 rounded-xl p-5"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Total Users</p>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-[#121212] border border-white/10 rounded-xl p-5"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Pending Approval</p>
                <p className="text-2xl font-bold text-amber-400">{stats.pending}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#121212] border border-white/10 rounded-xl p-5"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Approved</p>
                <p className="text-2xl font-bold text-emerald-400">{stats.approved}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Active Trial Customers — Prominent Section */}
        {users.filter(u => u.trial_active).length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <div className="bg-gradient-to-r from-violet-600/10 to-fuchsia-600/10 border-2 border-violet-500/30 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">Active Trial Customers</h3>
                    <p className="text-zinc-400 text-sm">{users.filter(u => u.trial_active).length} customer(s) on 7-day free trial with card on file</p>
                  </div>
                </div>
                <button onClick={() => setShowTrials(!showTrials)} className="text-violet-400 text-sm hover:text-violet-300">{showTrials ? "Hide" : "Show"}</button>
              </div>
              {showTrials && (
                <div className="space-y-2">
                  {users.filter(u => u.trial_active).map(u => {
                    const trialEnd = u.trial_end ? new Date(u.trial_end) : null;
                    const now = new Date();
                    const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - now) / 86400000)) : 0;
                    const isExpiring = daysLeft <= 2;
                    return (
                      <div key={u.user_id} className="flex items-center justify-between p-3 bg-black/30 rounded-lg border border-white/5">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${isExpiring ? "bg-amber-400 animate-pulse" : "bg-emerald-400"}`} />
                          <div>
                            <p className="text-white font-medium text-sm">{u.name} <span className="text-zinc-400">({u.email})</span></p>
                            <p className="text-zinc-500 text-xs">
                              Plan: <span className="text-violet-400">{u.subscription_plan || "—"}</span>
                              {u.subscription_amount ? <> · ${u.subscription_amount}</> : ""}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold text-sm ${isExpiring ? "text-amber-400" : "text-emerald-400"}`}>
                            {daysLeft > 0 ? `${daysLeft} day${daysLeft !== 1 ? "s" : ""} left` : "Trial ended"}
                          </p>
                          <p className="text-zinc-500 text-xs">
                            {trialEnd ? `Ends ${trialEnd.toLocaleDateString()}` : ""}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Users Table */}
        <Card className="bg-[#121212] border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white">Registered Users</CardTitle>
                <CardDescription className="text-zinc-400">
                  Approve or revoke user access
                </CardDescription>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  placeholder="Search users..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-black/50 border-white/10 text-white"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-zinc-400">User</TableHead>
                  <TableHead className="text-zinc-400">Email</TableHead>
                  <TableHead className="text-zinc-400">Status</TableHead>
                  <TableHead className="text-zinc-400">Registered</TableHead>
                  <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-zinc-400 py-8">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((u) => (
                    <TableRow key={u.user_id} className="border-white/5">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {u.picture ? (
                            <img
                              src={u.picture}
                              alt={u.name}
                              className="w-8 h-8 rounded-full"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center">
                              <span className="text-violet-400 font-semibold">
                                {u.name?.charAt(0)?.toUpperCase() || "U"}
                              </span>
                            </div>
                          )}
                          <span className="text-white font-medium">{u.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-zinc-400">{u.email}</TableCell>
                      <TableCell>
                        {u.trial_active ? (
                          <Badge className="bg-violet-500/10 text-violet-400 border-violet-500/30">
                            <Zap className="w-3 h-3 mr-1" />
                            Trial ({u.subscription_plan || "plan"})
                          </Badge>
                        ) : u.registered === "Y" ? (
                          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Approved
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30">
                            <Clock className="w-3 h-3 mr-1" />
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-zinc-400 text-sm">
                        {new Date(u.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {u.registered === "Y" ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleApprove(u.user_id, false)}
                              disabled={updating === u.user_id}
                              className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                            >
                              {updating === u.user_id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <UserX className="w-3 h-3 mr-1" />
                              )}
                              Revoke
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => handleApprove(u.user_id, true)}
                              disabled={updating === u.user_id}
                              className="bg-emerald-600 text-white hover:bg-emerald-700"
                            >
                              {updating === u.user_id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <UserCheck className="w-3 h-3 mr-1" />
                              )}
                              Approve
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeleteDialog(u)}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Delete User?</DialogTitle>
            <DialogDescription className="text-zinc-400">
              This will permanently delete {deleteDialog?.name} ({deleteDialog?.email}) and all their data.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog(null)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDelete(deleteDialog?.user_id)}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Early Access Signups */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setShowEarly(!showEarly)} className="flex items-center gap-2 text-white font-semibold">
            <Sparkles className="w-5 h-5 text-amber-400" />
            Turbo Early Access ({earlySignups.filter(s => s.verified).length} verified)
            <span className="text-zinc-400 text-sm ml-2">{showEarly ? "Hide" : "Show"}</span>
          </button>
        </div>
        {showEarly && (
          <div className="bg-[#121212] border border-white/10 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5 text-xs text-zinc-400 uppercase tracking-wider">
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Date</th>
                </tr>
              </thead>
              <tbody>
                {earlySignups.map((s) => (
                  <tr key={s.signup_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-sm text-white">{s.first_name}</td>
                    <td className="px-4 py-3 text-sm text-zinc-300">{s.email}</td>
                    <td className="px-4 py-3">
                      {s.verified
                        ? <span className="text-emerald-400 text-xs font-medium">Verified</span>
                        : <span className="text-amber-400 text-xs font-medium">Pending</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-400">{new Date(s.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {earlySignups.length === 0 && (
                  <tr><td colSpan="4" className="px-4 py-6 text-center text-zinc-500 text-sm">No signups yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
